import { useTheme } from "@emotion/react";
import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Modal,
  Slide,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { tryRegOrder } from "../../stateManagement/reducers/ordersReducer";
import AnimateButton from "../@extended/AnimateButton";
import MDBox from "../MDBox";
import MDTypography from "../MDTypography";
import CartItem from "./CartItem";
import borders from "../../assets/theme/base/borders";
import { position } from "stylis";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const CartModal = ({ isModalOpen, openModal, showOrderPlacementModal }) => {
  const toggleModal = () => {
    openModal(!isModalOpen);
  };
  const { authToken, user } = useSelector((state) => state.auth);
  const { cart } = useSelector((state) => state.cart);

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const theme = useTheme();
  const idGreaterThanMd = useMediaQuery(theme.breakpoints.up("md"));

  const [showCart, setShowCart] = useState(false);

  const placeOrder = () => {
    dispatch(
      tryRegOrder({
        authToken,
        data: {
          address: "",
          city: "",
          state: "",
          country: "",
          userIsCustomer: true,
          customerId: user.customerId,
          rentalItems: cart.items.map((cartItem) => ({
            _id: cartItem.rentalItem._id,
            count: cartItem.count,
          })),
        },
      }),
    );
  };

  const getCartTotalPrice = () => {
    let totalPrice = 0;

    for (const item of cart.items) {
      totalPrice += item.count * item.rentalItem.pricePerDay;
    }

    return totalPrice;
  };

  useEffect(() => {
    if (isModalOpen) {
      setShowCart(true);
    } else {
      setShowCart(false);
    }
  }, [isModalOpen]);

  return (
    <>
      <MDBox component="section" py={6} px={2}>
        <Container>
          <Modal
            open={isModalOpen}
            onClose={toggleModal}
            sx={{
              display: "grid",
              placeItems: "center",
              maxHeight: "100vh !important",
              maxWidth: "100vw !important",
              overflow: "hidden !important",
              p: "1rem !important",
            }}
          >
            <Slide direction="left" in={showCart} timeout={800}>
              <MDBox
                sx={{
                  maxWidth: "400px",
                  width: "100%",
                  height: "100vh",
                  backgroundColor: "#F5F0E6",
                  right: 0,
                  position: "absolute",
                  borderRadius: 0,
                  py: { xs: "1rem", md: 0 },
                }}
                display="flex"
                flexDirection="column"
                borderRadius="xl"
                shadow="xl"
              >
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  p={2}
                >
                  <MDTypography variant="h5" sx={{ fontWeight: "400" }}>
                    Cart
                  </MDTypography>
                  <AnimateButton>
                    <Close
                      fontSize="medium"
                      color="error"
                      sx={{ cursor: "pointer" }}
                      onClick={toggleModal}
                    />
                  </AnimateButton>
                </MDBox>
                <Divider textAlign="center" sx={{ my: 0 }} />
                {cart.items.length > 0 && (
                  <>
                    <div className="modal-body">
                      <Stack
                        alignItems="center"
                        spacing={4}
                        sx={{
                          overflowY: "auto",
                          maxHeight: "65vh !important",
                          px: ".5rem",
                          p: "1rem",
                        }}
                      >
                        {[...cart.items].reverse().map((cartItem) => (
                          <CartItem cartItem={cartItem} />
                        ))}
                      </Stack>
                    </div>
                    <Divider sx={{ my: 0 }} />
                    <Grid
                      container
                      direction="row"
                      spacing="space-between"
                      sx={{ mt: "1rem", px: "2rem" }}
                    >
                      <Grid item xs={6}>
                        <Typography
                          variant="h6"
                          display="block"
                          sx={{
                            fontFamily: "Inter",
                            fontSize: "1rem !important",
                            lineHeight: "1rem !important",
                            color: "black !important",
                          }}
                        >
                          Total:
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography
                          variant="h6"
                          display="block"
                          sx={{
                            fontFamily: "Inter",
                            fontSize: "1rem !important",
                            lineHeight: "1rem !important",
                            color: "black !important",
                            textAlign: "right",
                          }}
                        >
                          {`$${getCartTotalPrice()}`}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      direction="row"
                      spacing="space-between"
                      sx={{ my: "2rem", px: "2rem" }}
                    >
                      <Grid item xs={6}>
                        <AnimateButton>
                          <Button
                            variant="contained"
                            size={idGreaterThanMd ? "medium" : "small"}
                            color="info"
                            sx={{
                              background:
                                "linear-gradient(to top, #8b0000, #ff5e57) !important",
                              borderRadius: borders.borderRadius.md,
                            }}
                            onClick={() => {
                              openModal(false);
                            }}
                          >
                            <b>Exit Cart</b>
                          </Button>
                        </AnimateButton>
                      </Grid>
                      <Grid item xs={6} sx={{ display: "flex" }}>
                        <Box component="span" sx={{ ml: "auto !important" }}>
                          <AnimateButton>
                            <Button
                              variant="contained"
                              size={idGreaterThanMd ? "medium" : "small"}
                              color="info"
                              sx={{
                                background:
                                  "linear-gradient(to top,#2b6e3f, #4caf50) !important",
                                borderRadius: borders.borderRadius.md,
                                ml: "auto",
                              }}
                              onClick={() => {
                                if (!user) {
                                  return navigate("/login", { replace: true });
                                }

                                openModal(false);
                                showOrderPlacementModal();
                              }}
                            >
                              <b>Checkout</b>
                            </Button>
                          </AnimateButton>
                        </Box>
                      </Grid>
                    </Grid>
                  </>
                )}
                {!cart.items.length && (
                  <div className="modal-body flex align-center justify-center">
                    <Stack
                      alignItems="center"
                      spacing={4}
                      sx={{
                        overflowY: "auto",
                        height: "100% !important",
                        px: ".5rem",
                        m: "1rem",
                        mt: "auto",
                        mb: "0",
                      }}
                    >
                      <h3 className="mb-0 my-auto">Your Cart is empty.</h3>
                      <AnimateButton>
                        <Button
                          variant="contained"
                          size="large"
                          color="info"
                          sx={{
                            background:
                              "linear-gradient(to top, #8b0000, #ff5e57) !important",
                            borderRadius: borders.borderRadius.md,
                          }}
                          onClick={() => {
                            openModal(false);
                          }}
                          className="mb-0"
                        >
                          <b>Exit Cart</b>
                        </Button>
                      </AnimateButton>
                    </Stack>
                  </div>
                )}
              </MDBox>
            </Slide>
          </Modal>
        </Container>
      </MDBox>
    </>
  );
};

export default CartModal;
